import useResizeObserver from '@react-hook/resize-observer';
import classNames from 'classnames';
import fscreen from 'fscreen';
import Image from 'next/legacy/image';
import React, {
  CSSProperties,
  ReactElement,
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {LeaderboardIcon} from '../../../resources/Icons';
import envirocropsColoured from '../../../resources/img/envirocropsColoured.svg';
import Col from '../../common/Col';
import Row from '../../common/Row';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {useAppDispatch} from '../../store';
import {GameSettingsActions} from '../gameSettingsSlice';
import {Actions} from '../gameSlice';
import {useCurrentEvent, useCurrentQuestionComputed, useGameStatus} from '../hooks';
import {useGameSettings} from '../hooks/useGameSettings';
import {getWalkthroughById} from '../resources/walkthrough';
import {GamePhase} from '../types';
import Background from './Background';
import Balance from './Balance';
import GameMenuButtons from './buttons/GameMenuButtons';
import NormalButton from './buttons/NormalButton';
import {FeedbackButton, LeaderboardButton} from './buttons/PillButtons';
import End from './End';
import Events from './Events';
import FixedBalance from './FixedBalance';
import Harvest from './Harvest';
import HelpMenu from './HelpMenu';
import Jeopardies from './Jeopardies';
import Landing from './Landing';
import Leaderboard from './Leaderboard';
import Preparation from './Preparation';
import ScrollBar from './ScrollBar';
import Timeline from './Timeline';
import Toolbox from './Toolbox';
import Walkthrough from './Walkthrough';
import Years from './Years';
import YearWrapUp from './YearWrapUp';

const SCROLLBAR_DISABLE_OFFSET = 10; // px

type Props = {
  style?: CSSProperties;
  fullscreenElement: RefObject<HTMLElement>;
};

const Board = ({fullscreenElement, style}: Props): ReactElement => {
  const {welcome, preparation, questions, jeopardies, events, wrapup, harvest, finished} = GamePhase;

  const {isMobile} = useBreakpoint('mobile');
  const gameStatus = useGameStatus();
  const currentQuestion = useCurrentQuestionComputed();
  const {currentEvent} = useCurrentEvent();
  const settings = useGameSettings();
  const dispatch = useAppDispatch();
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const boardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const balanceRef = useRef<HTMLDivElement>(null);
  const [scrollBarEnabled, setScrollBarEnabled] = useState(false);

  const walkthrough = useMemo(
    () => getWalkthroughById(gameStatus.walkthroughProgress.current),
    [gameStatus.walkthroughProgress]
  );

  const handleScroll = useCallback((e: Element) => {
    const atBottom = e.scrollHeight - e.scrollTop - e.clientHeight < SCROLLBAR_DISABLE_OFFSET;
    setScrollBarEnabled(!atBottom);
  }, []);

  useResizeObserver(contentRef, _entry => {
    boardRef.current && handleScroll(boardRef.current);
  });

  const handleBoardScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement, UIEvent>) => handleScroll(e.target as Element),
    [handleScroll]
  );

  const handleToggleFullscreen = useCallback(() => {
    if (!fullscreenElement.current) return;
    if (!fscreen.fullscreenEnabled) alert('Full screen not supported in this device');

    if (!fscreen.fullscreenElement) {
      fscreen.requestFullscreen(fullscreenElement.current);
    } else {
      fscreen.exitFullscreen();
    }
  }, [fullscreenElement]);

  const handleBoardClick = useCallback(async () => {
    if (!settings.canPlayAudio) {
      dispatch(GameSettingsActions.activateAudio());
    }
  }, [dispatch, settings.canPlayAudio]);

  const handleRestart = useCallback(() => {
    setShowLeaderboard(false);
    dispatch(Actions.restart());
  }, [dispatch]);

  const MenuButtons = useCallback(
    () => (
      <GameMenuButtons
        audioToggled={settings.audioEnabled}
        fullscreenToggled={!!fscreen.fullscreenElement}
        hideFullscreenButton={!fscreen.fullscreenEnabled}
        hideRestartButton={gameStatus.phase === GamePhase.welcome}
        onAudioClick={() => dispatch(GameSettingsActions.toggleAudio())}
        onFullscreenClick={handleToggleFullscreen}
        onRestartClick={handleRestart}
      />
    ),
    [settings.audioEnabled, gameStatus.phase, handleToggleFullscreen, handleRestart, dispatch]
  );

  // Reset content scroll whenever game phase or current question change
  useLayoutEffect(() => {
    void gameStatus.phase; // Force hook dependency
    void currentQuestion?.id; // Force hook dependency
    void currentEvent?.id; // Force hook dependency
    boardRef.current?.scrollTo(0, 0);
  }, [currentEvent?.id, currentQuestion?.id, gameStatus.phase]);

  return (
    <>
      <div
        id={'envirogameBoard'}
        className={classNames('relative shrink-0 m-auto border-4 border-primary-800 overflow-hidden')}
        style={style}
        onClick={handleBoardClick}
      >
        <Background />
        {isMobile && gameStatus.phase >= questions && <FixedBalance triggerElement={balanceRef} />}
        <Row ref={boardRef} className={'h-full absolute inset-0 overflow-auto'} onScroll={handleBoardScroll}>
          {!isMobile && (
            <Col className={'w-1/5 pl-xl py-lg items-start grow-0 shrink-0'}>
              {gameStatus.phase >= preparation && (
                <div className={'w-[60%] h-xl'}>
                  <Image src={envirocropsColoured} alt={'Envirocrops logo'} layout={'intrinsic'} />
                </div>
              )}
              {gameStatus.phase >= questions && <Balance />}
            </Col>
          )}
          <Col className={classNames('flex-grow p-[2.08%]', isMobile ? 'gap-10 w-full' : 'gap-xl')}>
            {isMobile && (
              <Col className={'items-end gap-xs'}>
                <Row className="self-stretch justify-between items-center">
                  <div className="h-md">
                    {gameStatus.phase >= preparation && (
                      <Image src={envirocropsColoured} alt={'Envirocrops logo'} layout={'intrinsic'} />
                    )}
                  </div>
                  <MenuButtons />
                </Row>
                {gameStatus.phase === welcome && (
                  <NormalButton onClick={() => setShowLeaderboard(true)} aria-label={'Leaderboard'}>
                    <LeaderboardIcon className="h-sm w-sm" />
                  </NormalButton>
                )}
              </Col>
            )}
            {(!isMobile || gameStatus.phase !== welcome) && (
              <div className={gameStatus.phase < questions ? 'invisible' : ''}>
                <Timeline id="timeline" currentYear={gameStatus.currentYear + 1} totalYears={gameStatus.totalYears} />
              </div>
            )}
            {isMobile && gameStatus.phase >= questions && <Balance wrapperRef={balanceRef} />}
            <div ref={contentRef} className={'relative'}>
              {showLeaderboard && (
                <Leaderboard addScore={gameStatus.phase === finished} onClose={() => setShowLeaderboard(false)} />
              )}
              {!showLeaderboard && !showLeaderboard && gameStatus.phase === welcome && <Landing />}
              {!showLeaderboard && gameStatus.phase === preparation && <Preparation />}
              {!showLeaderboard && gameStatus.phase === questions && <Years />}
              {!showLeaderboard && gameStatus.phase === jeopardies && <Jeopardies />}
              {!showLeaderboard && gameStatus.phase === events && <Events />}
              {!showLeaderboard && gameStatus.phase === wrapup && <YearWrapUp />}
              {!showLeaderboard && gameStatus.phase === harvest && <Harvest />}
              {!showLeaderboard && gameStatus.phase === finished && (
                <End onAddScore={() => setShowLeaderboard(true)} onPlayAgain={handleRestart} />
              )}
            </div>
            {isMobile && gameStatus.phase === finished && (
              <div className="flex-grow w-full flex justify-center items-end -mt-10 pb-8">
                <FeedbackButton textOnly />
              </div>
            )}
          </Col>
          {!isMobile && (
            <Col className={'w-1/5 pr-xl py-lg items-end grow-0 shrink-0'}>
              <div className="h-xl">
                <MenuButtons />
              </div>
              <Col className="mt-xl gap-y-sm items-end">
                {gameStatus.phase === questions && <Toolbox />}
                {gameStatus.phase >= questions && <FeedbackButton />}
                {!showLeaderboard && gameStatus.phase === welcome && (
                  <LeaderboardButton onClick={() => setShowLeaderboard(true)} />
                )}
                {gameStatus.phase === questions && <HelpMenu />}
              </Col>
            </Col>
          )}
        </Row>
        <ScrollBar enabled={scrollBarEnabled} />
      </div>
      <Walkthrough
        walkthrough={walkthrough}
        onContinueButtonClick={() => dispatch(Actions.nextWalkthrouh())}
        onBackButtonClick={() => dispatch(Actions.previousWalkthrouh())}
        onSkipButtonClick={() => dispatch(Actions.skipWalkthrouh())}
      />
    </>
  );
};

export default Board;
